import { useCallback, useContext } from 'react';
import { AudioContext } from 'services/contexts/AudioProvider';

/**
 * Enum representing HTMLMediaElement.readyState possible values
 *
 * WHATWG spec link:
 * {@link https://html.spec.whatwg.org/multipage/media.html#dom-media-readystate-dev}
 */
enum MediaReadyState {
  HAVE_NOTHING = 0,
  HAVE_METADATA = 1,
  HAVE_CURRENT_DATA = 2,
  HAVE_FUTURE_DATA = 3,
  HAVE_ENOUGH_DATA = 4,
}

const ensureCanPlay = async (audioElement: HTMLAudioElement) => {
  if (audioElement.readyState === MediaReadyState.HAVE_ENOUGH_DATA) {
    return true;
  }
  return new Promise((resolve) => {
    audioElement.addEventListener('canplaythrough', resolve, {
      once: true,
    });
  });
};

const useAudio = () => {
  const { audioElement } = useContext(AudioContext);
  const setSrc = useCallback(
    (src: string) => {
      if (!audioElement) {
        return;
      }
      audioElement.src = src;
    },
    [audioElement]
  );

  const play = useCallback(async () => {
    if (!audioElement) {
      return;
    }

    await ensureCanPlay(audioElement);
    await audioElement.play();
  }, [audioElement]);

  const pause = useCallback(() => {
    if (!audioElement) {
      return;
    }
    audioElement.pause();
  }, [audioElement]);

  const seek = useCallback(
    async (value: number) => {
      if (!audioElement) {
        return;
      }
      await ensureCanPlay(audioElement);
      audioElement.currentTime = value * audioElement.duration;
    },
    [audioElement]
  );

  return {
    audioElement,
    setSrc,
    play,
    pause,
    seek,
  };
};

export default useAudio;
